import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLoadAppData } from '../../../hooks/useLoadAppData';
import { bulkUploadTrailers } from '../../../store/trailers';

const BulkUploadTrailers = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [showTemplate, setShowTemplate] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  const sessionUser = useSelector(state => state.session.user);
  const accounts = useSelector(state => Object.values(state.accounts || {}));

  // Get bulk upload state from Redux store
  const bulkUploadState = useSelector(state => state.trailers?.bulkUpload);
  const isUploading = bulkUploadState?.status === 'started';
  const results = bulkUploadState?.status === 'succeeded' ? bulkUploadState.results : null;

  // Clear error when component unmounts or when file changes
  useEffect(() => {
    setError('');
  }, [file]);

  // Use the hook to load all app data including accounts
  const isLoading = useLoadAppData();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    // Check if admin user has selected an account
    if (sessionUser?.is_admin && !selectedAccountId) {
      setError('Please select an account for these trailers');
      return;
    }

    // Check file extension
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xls', 'xlsx'].includes(fileExt)) {
      setError('Please upload a CSV or Excel file (.csv, .xls, .xlsx)');
      return;
    }

    // Create a fresh FormData object
    const formData = new FormData();

    // Add the file with the key 'file' that the server expects
    formData.append('file', file);

    // Add account_id to the request if user is admin
    if (sessionUser?.is_admin && selectedAccountId) {
      formData.append('account_id', selectedAccountId);
    }

    // Log FormData to verify (FormData can't be directly logged)
    console.log('Submitting file:', file.name, 'with size:', file.size);

    try {
      // Dispatch the bulk upload action
      const result = await dispatch(bulkUploadTrailers(formData));

      if (!result.success) {
        setError(result.error);
      }
    } catch (error) {
      console.error('Error during bulk upload:', error);
      setError(error.message || 'An error occurred during upload');
    }
  };

  const downloadTemplateCSV = () => {
    const headers = [
      'trailer_number',
      'trailer_location',
      'company_owner',
      'license_plate',
      'license_state',
      'type',
      'is_loaded',
      'load_number',
      'is_tray',
      'tray_count',
      'yellow_tagged',
      'red_tagged',
      'tagged_description',
      'in_shop'
    ];

    // Create two sample rows to demonstrate different use cases
    const sampleRows = [
      // Regular trailer with load
      'T12345,Yard,Company XYZ,ABC123,CA,dry_53,TRUE,LOAD123,FALSE,0,FALSE,FALSE,,FALSE',
      // Trailer with trays and yellow tag
      'T67890,DC-4,Company XYZ,XYZ789,NY,reefer_48,FALSE,,TRUE,5,TRUE,FALSE,Needs repairs,FALSE'
    ];

    const csvContent = headers.join(',') + '\n' + sampleRows.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'trailer_upload_template.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Show loading state while data is being fetched
  if (isLoading) {
    return <div className="p-5 text-7xl">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-8">
      <h1 className="text-4xl font-bold mb-6">Bulk Upload Trailers</h1>

      <div className="mb-8 p-6 bg-blue-50 rounded-lg shadow">
        <h2 className="text-3xl font-semibold mb-4">Instructions</h2>
        <p className="mb-4 text-xl">Upload a CSV or Excel file with trailer information to add multiple trailers at once.</p>

        <div className="mb-4">
          <button
            onClick={() => setShowTemplate(!showTemplate)}
            className="text-blue-600 underline font-medium"
          >
            {showTemplate ? 'Hide Template Information' : 'Show Template Information'}
          </button>

          {showTemplate && (
            <div className="mt-4 text-2xl p-4 bg-white rounded border border-gray-300">
              <p className="mb-2 font-semibold">Required Fields:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>trailer_number - Unique identifier for the trailer</li>
                <li>trailer_location - Current location of the trailer</li>
                <li>company_owner - Company that owns the trailer</li>
              </ul>

              <p className="mb-2 font-semibold">Optional Fields:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>license_plate - License plate number</li>
                <li>license_state - Two-letter state code</li>
                <li>type - Trailer type (e.g., dry_53, reefer_48) - see valid types below</li>
                <li>is_loaded - TRUE or FALSE (also accepts yes/y/1 or no/n/0)</li>
                <li>load_number - Required if is_loaded is TRUE</li>
                <li>is_tray - TRUE or FALSE (also accepts yes/y/1 or no/n/0)</li>
                <li>tray_count - Number of trays (should be {'>'}0 if is_tray is TRUE)</li>
                <li>yellow_tagged - TRUE or FALSE (requires tagged_description if TRUE)</li>
                <li>red_tagged - TRUE or FALSE (requires tagged_description if TRUE)</li>
                <li>tagged_description - Required if yellow_tagged or red_tagged is TRUE</li>
                <li>in_shop - TRUE or FALSE</li>
              </ul>

              <p className="mb-2 text-blue-600">Important Notes:</p>
              <ul className="list-disc ml-6 mb-4">
                <li>If tagged_description is provided but neither yellow_tagged nor red_tagged is TRUE, yellow_tagged will be set to TRUE automatically</li>
                <li>If either yellow_tagged or red_tagged is TRUE without a tagged_description, a placeholder description will be added</li>
                <li>All boolean fields accept TRUE/FALSE, Yes/No, Y/N, 1/0 (case insensitive)</li>
              </ul>

              <p className="mb-2 font-semibold">Valid Trailer Types:</p>
              <p className="mb-4">
                dry_53, dry_48, dry_42, dry_pup, reefer_53, reefer_48, reefer_42, reefer_pup,
                liftgate_53, liftgate_48, liftgate_42, liftgate_pup, intermodal_53, intermodal_48,
                intermodal_42, intermodal_pup
              </p>

              <button
                onClick={downloadTemplateCSV}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                Download Template CSV
              </button>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mb-8">
        {/* Account selection for admin users */}
        {sessionUser?.is_admin && (
          <div className="mb-6">
            <label htmlFor="account-select" className="block text-xl font-medium mb-2">
              Select Account <span className="text-red-500">*</span>
            </label>
            <select
              id="account-select"
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="block w-full text-gray-700 border border-gray-300 rounded py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select an account</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mb-6">
          <label htmlFor="file-upload" className="block text-xl font-medium mb-2">
            Upload File (CSV or Excel)
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".csv, .xls, .xlsx"
            onChange={handleFileChange}
            className="block w-full text-gray-700 border border-gray-300 rounded py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {error && <p className="text-red-600 mt-2">{error}</p>}
        </div>

        <div className="flex gap-4 justify-between text-xl">
          <button
            type="button"
            onClick={() => history.push('/home')}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isUploading}
            className={`px-6 py-3 rounded-lg text-white font-semibold ${
              isUploading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isUploading ? 'Uploading...' : '+ Add Upload Trailers'}
          </button>
        </div>
      </form>

      {results && (
        <div className="p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold mb-4">Upload Results</h2>

          <div className="flex flex-wrap gap-6 mb-6 md:flex-row">
            <div className="min-w-[180px] p-4 bg-green-100 rounded-lg text-center">
              <p className="text-lg font-medium">Total Records</p>
              <p className="text-3xl font-bold">{results.total}</p>
            </div>

            <div className="min-w-[180px] p-4 bg-green-100 rounded-lg text-center">
              <p className="text-lg font-medium">Successfully Added</p>
              <p className="text-3xl font-bold text-green-600">{results.success}</p>
            </div>

            <div className="min-w-[180px] p-4 bg-red-100 rounded-lg text-center">
              <p className="text-lg font-medium">Errors</p>
              <p className="text-3xl font-bold text-red-600">{results.errors.length}</p>
            </div>
          </div>

          {results.errors.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold mb-3">Error Details</h3>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-3 text-left border border-gray-300">Row</th>
                      <th className="p-3 text-left border border-gray-300">Trailer Number</th>
                      <th className="p-3 text-left border border-gray-300">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.errors.map((error, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-3 border border-gray-300">{error.row}</td>
                        <td className="p-3 border border-gray-300">{error.trailer_number}</td>
                        <td className="p-3 border border-gray-300">{error.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-6">
            <button
              onClick={() => history.push('/home')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUploadTrailers;
